import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Wind, Activity, Sparkles, Camera, Info, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { useFacialScan } from '../../contexts/FacialScanContext';
import { toast } from 'sonner';

export default function ScanStartPage() {
  const navigate = useNavigate();
  const { startScan, beginCountdown, SCAN_STATE, scanState } = useFacialScan();
  const [showInstructions, setShowInstructions] = useState(true);
  const [cameraReady, setCameraReady] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [lightingOk, setLightingOk] = useState(true);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Initialize scan on mount (auth is handled by ProtectedRoute in App.js)
  useEffect(() => {
    startScan();
    initCamera();
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Simulate face detection after camera starts
  useEffect(() => {
    if (cameraReady) {
      const faceTimer = setTimeout(() => {
        setFaceDetected(true);
      }, 1500);
      return () => clearTimeout(faceTimer);
    }
  }, [cameraReady]);

  const initCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraReady(true);
    } catch (err) {
      console.error('Camera error:', err);
      // Still allow mock experience - camera may not be available in test env
      setCameraReady(true);
    }
  };

  const handleStartScan = () => {
    if (!faceDetected) {
      toast.error('Please center your face in the frame');
      return;
    }
    beginCountdown();
    navigate('/scan/in-progress');
  };

  const handleClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    navigate('/dashboard');
  };

  const metrics = [
    { icon: Heart, label: 'Heart Rate', color: '#D54B48' },
    { icon: Wind, label: 'Breathing', color: '#8BB89F' },
    { icon: Activity, label: 'HRV', color: '#F8B64C' },
    { icon: Sparkles, label: 'Stress Index', color: '#C10891' }
  ];

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="rounded-full bg-black/30 text-white hover:bg-black/50"
          data-testid="close-scan-btn"
        >
          <X size={24} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowInstructions(true)}
          className="rounded-full bg-black/30 text-white hover:bg-black/50"
        >
          <Info size={20} />
        </Button>
      </header>

      {/* Camera View */}
      <div className="flex-1 relative overflow-hidden">
        {/* Video Element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
        
        {/* Fallback gradient if no camera */}
        {!streamRef.current && (
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-800" />
        )}

        {/* Oval Face Frame */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative"
          >
            {/* Outer glow */}
            <div 
              className={`absolute inset-0 rounded-[50%] transition-all duration-500 ${
                faceDetected ? 'shadow-[0_0_40px_rgba(139,184,159,0.5)]' : 'shadow-[0_0_40px_rgba(193,8,145,0.3)]'
              }`}
              style={{ width: 260, height: 340 }}
            />
            
            {/* Frame border */}
            <svg width="260" height="340" viewBox="0 0 260 340">
              <defs>
                <linearGradient id="frameGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={faceDetected ? '#8BB89F' : '#C10891'} />
                  <stop offset="100%" stopColor={faceDetected ? '#8BB89F' : '#6D2A83'} />
                </linearGradient>
              </defs>
              <ellipse
                cx="130"
                cy="170"
                rx="125"
                ry="165"
                fill="none"
                stroke="url(#frameGradient)"
                strokeWidth="4"
                strokeDasharray={faceDetected ? "0" : "20 10"}
                className="transition-all duration-500"
              />
              
              {/* Corner markers */}
              {!faceDetected && (
                <>
                  <path d="M 50 70 L 50 40 Q 50 20 70 20 L 100 20" fill="none" stroke="#C10891" strokeWidth="3" strokeLinecap="round" />
                  <path d="M 210 70 L 210 40 Q 210 20 190 20 L 160 20" fill="none" stroke="#C10891" strokeWidth="3" strokeLinecap="round" />
                  <path d="M 50 270 L 50 300 Q 50 320 70 320 L 100 320" fill="none" stroke="#C10891" strokeWidth="3" strokeLinecap="round" />
                  <path d="M 210 270 L 210 300 Q 210 320 190 320 L 160 320" fill="none" stroke="#C10891" strokeWidth="3" strokeLinecap="round" />
                </>
              )}
            </svg>

            {/* Status indicator */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute -bottom-12 left-1/2 -translate-x-1/2 whitespace-nowrap"
            >
              {faceDetected ? (
                <span className="text-[#8BB89F] text-sm font-medium flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#8BB89F] animate-pulse" />
                  Face detected - Ready to scan
                </span>
              ) : (
                <span className="text-white/70 text-sm">Center your face in the frame</span>
              )}
            </motion.div>
          </motion.div>
        </div>

        {/* Metrics to capture */}
        <div className="absolute bottom-32 left-0 right-0 px-6">
          <div className="flex justify-center gap-4">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <motion.div
                  key={metric.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex flex-col items-center gap-1"
                >
                  <div 
                    className="p-2 rounded-xl"
                    style={{ backgroundColor: `${metric.color}20` }}
                  >
                    <Icon size={18} style={{ color: metric.color }} />
                  </div>
                  <span className="text-white/60 text-[10px]">{metric.label}</span>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Guidance text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="absolute top-24 left-0 right-0 text-center px-6"
        >
          <p className="text-white text-lg font-medium">Hold still</p>
          <p className="text-white/60 text-sm">Measuring your heart and stress</p>
        </motion.div>
      </div>

      {/* Bottom Actions */}
      <div className="bg-black/80 backdrop-blur-lg p-6 pb-10">
        <Button
          onClick={handleStartScan}
          disabled={!cameraReady}
          className={`w-full rounded-full py-6 text-lg font-semibold transition-all ${
            faceDetected 
              ? 'bg-[#8BB89F] hover:bg-[#8BB89F]/90' 
              : 'bg-primary hover:bg-primary/90'
          }`}
          data-testid="start-scan-btn"
        >
          {faceDetected ? 'Start Scan' : 'Waiting for face...'}
        </Button>
        
        {!lightingOk && (
          <div className="flex items-center justify-center gap-2 mt-3 text-[#F8B64C]">
            <AlertCircle size={14} />
            <span className="text-sm">More light needed</span>
          </div>
        )}
      </div>

      {/* Instructions Modal */}
      <AnimatePresence>
        {showInstructions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
            >
              <Card className="glass-card p-6 max-w-sm">
                <div className="text-center mb-6">
                  <div className="p-4 rounded-full bg-primary/20 w-fit mx-auto mb-4">
                    <Camera size={32} className="text-primary" />
                  </div>
                  <h2 className="font-heading font-bold text-xl mb-2">Facial Scan</h2>
                  <p className="text-sm text-muted-foreground">
                    We'll measure your vitals using your camera. Takes about 10 seconds.
                  </p>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded-full bg-[#8BB89F]/20 mt-0.5">
                      <span className="text-xs text-[#8BB89F]">1</span>
                    </div>
                    <p className="text-sm">Find good lighting (avoid harsh shadows)</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded-full bg-[#8BB89F]/20 mt-0.5">
                      <span className="text-xs text-[#8BB89F]">2</span>
                    </div>
                    <p className="text-sm">Remove glasses, hats, or face coverings</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded-full bg-[#8BB89F]/20 mt-0.5">
                      <span className="text-xs text-[#8BB89F]">3</span>
                    </div>
                    <p className="text-sm">Stay still during the scan</p>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground text-center mb-4">
                  Your camera feed is processed locally and never stored.
                </p>

                <Button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-primary hover:bg-primary/90 rounded-full"
                  data-testid="continue-instructions-btn"
                >
                  Continue
                </Button>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
